import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalPanel,
} from '../Common/ModalShell';
import {
  createMembershipFeature,
  updateMembershipFeature,
  type MembershipFeatureRow,
} from '../../lib/membershipTiers';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

const labelClass = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

const SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export default function MembershipFeatureModal({
  isOpen,
  onClose,
  feature,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  feature: MembershipFeatureRow | null;
  onSuccess: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (feature) {
      setSlug(feature.slug);
      setLabel(feature.label);
      setSortOrder(feature.sortOrder);
      setActive(feature.active);
    } else {
      setSlug('');
      setLabel('');
      setSortOrder(0);
      setActive(true);
    }
  }, [isOpen, feature]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleSave = async () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      toast.error('Label is required');
      return;
    }

    setSaving(true);
    try {
      if (feature) {
        await updateMembershipFeature(feature.id, { label: trimmedLabel, sortOrder, active });
        toast.success('Benefit updated');
      } else {
        const normalizedSlug = slug.trim() || slugify(trimmedLabel);
        if (!SLUG_RE.test(normalizedSlug)) {
          toast.error('Slug must be lowercase letters, digits, hyphens or underscores');
          setSaving(false);
          return;
        }
        await createMembershipFeature({
          slug: normalizedSlug,
          label: trimmedLabel,
          sortOrder,
          active,
        });
        toast.success('Benefit created');
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClose={onClose}>
      <ModalPanel maxWidthClass="max-w-lg" titleId="feature-modal-title">
        <ModalHeader
          title={feature ? 'Edit benefit' : 'New benefit'}
          titleId="feature-modal-title"
          onClose={onClose}
          description="Wording is shared by every tier that lists it."
        />

        <ModalBody className="space-y-4">
          <div>
            <label className={labelClass} htmlFor="feature-label">
              Label
            </label>
            <input
              id="feature-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => {
                if (!feature && label.trim() && !slug.trim()) setSlug(slugify(label));
              }}
              className={inputClass}
              placeholder="Weekly progress reviews"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="feature-slug">
                Slug
              </label>
              <input
                id="feature-slug"
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                disabled={Boolean(feature)}
                className={`${inputClass} font-mono disabled:opacity-60 disabled:cursor-not-allowed`}
                placeholder="weekly-reviews"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="feature-sort">
                Sort order
              </label>
              <input
                id="feature-sort"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            Active
          </label>
        </ModalBody>

        <ModalFooter className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
}
